// src/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { getAuthStatus, logoutUser, getUserSpinCount } from './api'; // <-- Import getUserSpinCount
import { useSnackbar } from 'notistack'; // <-- Import useSnackbar
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'; // <-- Import icon for notification

// Base URL (keep consistent)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.spinnercs.com';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const { enqueueSnackbar } = useSnackbar(); // <-- Get snackbar function

    // --- REMOVED: Login Modal State ---
    // const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    // --- END REMOVED ---

    const intervalRef = useRef(null); // <-- Ref to store interval ID
    const prevSpinCountRef = useRef(null); // <-- Ref to store previous spin count

    // Initial Auth Check
    useEffect(() => {
        const checkAuth = async () => {
            setLoading(true);
            try {
                const userData = await getAuthStatus();
                setUser(userData);
                // Initialize previous spin count after first load
                if (userData?.isAuthenticated) {
                    prevSpinCountRef.current = userData.spinCount ?? null;
                } else {
                    prevSpinCountRef.current = null;
                }
            } catch (error) {
                console.error("Initial auth check failed:", error);
                setUser({ isAuthenticated: false }); // Ensure user is marked as not authenticated on error
                prevSpinCountRef.current = null;
            } finally {
                setLoading(false);
            }
        };
        checkAuth();
    }, []);

    // Effect for periodic spin count fetching
    useEffect(() => {
        // Function to fetch and compare spins
        const fetchAndCheckSpins = async () => {
            if (!user?.isAuthenticated) return; // Only run if authenticated

            try {
                const currentSpinCount = await getUserSpinCount(); // API returns the number directly

                // Check if previous count exists and if the new count is higher
                if (prevSpinCountRef.current !== null && currentSpinCount > prevSpinCountRef.current) {
                    const diff = currentSpinCount - prevSpinCountRef.current;
                    enqueueSnackbar(`You received ${diff} spin${diff > 1 ? 's' : ''}!`, {
                        variant: 'success',
                        anchorOrigin: { vertical: 'top', horizontal: 'right' }, // Position top-right
                        autoHideDuration: 5000, // 5 seconds
                        // Optional: Add icon
                        // action: (key) => <ConfirmationNumberIcon sx={{ color: 'white', mr: 1 }}/>
                    });
                    // Send conversion to Google Ads
                    if (typeof window.gtag === 'function') {
                        window.gtag('event', 'conversion', { 'send_to': 'AW-17081467546/mRRxCJf69MkaEJqFitE_' });
                    } else {
                        console.warn('gtag function not found. Google Ads conversion not sent.');
                    }
                }

                // Update user state in context if count differs
                if (currentSpinCount !== user.spinCount) {
                    setUser(prevUser => ({ ...prevUser, spinCount: currentSpinCount }));
                }
                // Update the previous count ref
                prevSpinCountRef.current = currentSpinCount;
            } catch (error) {
                console.error("Error fetching spin count periodically:", error);
                // Optionally stop interval if it fails repeatedly, or just log
            }
        };

        // Start interval only when user is authenticated
        if (user?.isAuthenticated) {
            // Clear any existing interval first
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            // Fetch immediately on login/refresh, then start interval
            fetchAndCheckSpins();
            intervalRef.current = setInterval(fetchAndCheckSpins, 30000); // Fetch every 30 seconds
        } else {
            // Clear interval if user logs out
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            prevSpinCountRef.current = null; // Reset previous count on logout
        }

        // Cleanup function to clear interval on component unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [user?.isAuthenticated, user?.spinCount, enqueueSnackbar]); // Rerun when auth status or spin count changes

    const login = () => {
        window.location.href = `${API_BASE_URL}/auth/login`;
    };

    const logout = async () => {
        await logoutUser();
        setUser({ isAuthenticated: false }); // Update state immediately
        // Interval cleanup is handled by the useEffect hook based on user.isAuthenticated
    };

    // --- REMOVED: Login Modal Functions ---
    // const openLoginModal = () => setIsLoginModalOpen(true);
    // const closeLoginModal = () => setIsLoginModalOpen(false);
    // --- END REMOVED ---

    // Modified refreshUser to also update prevSpinCountRef
    const refreshUser = useCallback(async () => {
        // setLoading(true); // Avoid global loading for background refresh
        try {
            const userData = await getAuthStatus();
            setUser(userData);
            // Update previous spin count after refresh
            if (userData?.isAuthenticated) {
                prevSpinCountRef.current = userData.spinCount ?? null;
            } else {
                prevSpinCountRef.current = null;
            }
        } catch (error) {
            console.error("Error refreshing user data:", error);
            // Handle error appropriately, maybe logout user if refresh fails critically
            setUser({ isAuthenticated: false });
            prevSpinCountRef.current = null;
        } finally {
            // setLoading(false); // Only set loading false if it was set true initially
        }
    }, []); // Removed setLoading dependency if not used

    return (
        <AuthContext.Provider value={{
            user,
            login,
            logout,
            loading,
            refreshUser,
            // --- REMOVED: Login Modal related values from context ---
            // isLoginModalOpen,
            // openLoginModal,
            // closeLoginModal,
            // --- END REMOVED ---
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
